import { useState, useRef, useEffect } from 'react';
import LanguageSelector from 'Components/LanguageSelector';
import { Select, Button } from 'Components/ui';
import { DotsLoader } from 'Components/DotsLoader';
import styled from 'styled-components';
import {
  Main, HamburgerButton, TenantChip, RightSection,
  NotifBadge, UserButton, UserInitialsAvatar, UserName,
  UserDropdown, DropdownUserInfo, DropdownUserAvatar, DropdownUserMeta,
  CompanySelectWrapper, CompanySelectLabel,
  LogoutItem, TenantBanner, BannerText, BannerSub, BannerBackButton,
  NotificationDropdown, NotifDropdownHeader,
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { logoff, switchTenantSuccess, useAuthTenant, useAuthUser } from 'store/auth';
import { useTranslation } from 'react-i18next';
import { getSelectedObject } from 'utils/select_utils';
import { getTenant, canSwitchTenant } from 'utils/tenant_utils';
import { switchCompany as switchCompanyApi, getCompaniesWithAccess } from 'services/auth-service';
import { changeStatusMenu } from 'store/menu';
import { useQuery, useQueryClient } from 'react-query';
import { getTotalCountUserNotification, getUserNotifications } from 'services/user-notifications-service';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';

const NotifItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--vo-cloud);
  cursor: pointer;
  &:hover { background: var(--vo-mist); }
`

const NotifItemBody = styled.div`
  flex: 1;
  min-width: 0;
  .title { font-family: 'Maven Pro', sans-serif; font-size: 13px; font-weight: 600; color: var(--vo-ink); }
  .body { font-family: 'Lato', sans-serif; font-size: 12px; color: var(--vo-slate); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .time { font-family: 'Lato', sans-serif; font-size: 11px; color: var(--vo-slate); margin-top: 3px; }
`

const NotifEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`

const HamburgerSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
)

const BellSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
)

const BuildingSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <line x1="9" y1="3" x2="9" y2="21"/>
    <line x1="15" y1="3" x2="15" y2="21"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="3" y1="15" x2="21" y2="15"/>
  </svg>
)

const WarningSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
)

const ChevronSvg = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
)

const LogoutSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
)

const getInitials = (name) => {
  if (!name) return 'U'
  const parts = name.trim().split(' ').filter(Boolean)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export const Header = ({ openMenu }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  // SPEC-AUTH-003: local-only UI state for the real switch-company flow — deliberately never
  // persisted (store/index.js persists the whole `auth` reducer with no per-field blacklist, so
  // a persisted in-flight flag would survive a page refresh mid-switch with nothing to clear it).
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchTarget, setSwitchTarget] = useState(null);
  const [switchError, setSwitchError] = useState(null);
  // FR-7: overrides the login-time companiesWithAccess list once the dropdown is first opened.
  const [freshCompaniesWithAccess, setFreshCompaniesWithAccess] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // The tenant Select's menu renders in a MUI portal appended to
      // document.body, outside userMenuRef's subtree — without this check
      // a mousedown on one of its options reads as "outside" and closes
      // the user menu before the selection's onChange can fire.
      if (e.target.closest('.MuiModal-root')) return;
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    if (notifOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  const onLogoff = () => {
    queryClient.clear();
    dispatch(logoff());
  };

  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);

  // SPEC-AUTH-003 FR-1/FR-2/FR-4/FR-5/FR-6: the one real, server-validated switch-company flow,
  // shared by both the dropdown selection and the banner's "back to my tenant" button. Dispatch
  // only happens after the await resolves, inside the try block — never optimistic — so a
  // failure leaves Redux/localStorage completely untouched.
  const handleSwitchCompany = async (company) => {
    if (isSwitching || !company) return;
    setIsSwitching(true);
    setSwitchError(null);
    setSwitchTarget(company);
    try {
      const response = await switchCompanyApi(company.slug ?? company._id);
      dispatch(switchTenantSuccess({
        token: response.data.token,
        refreshToken: response.data.refreshToken,
        tenant: company,
      }));
      setSwitchTarget(null);
    } catch (error) {
      setSwitchError(error?.response?.data?.message ?? 'unknown');
    } finally {
      setIsSwitching(false);
      setUserMenuOpen(false);
    }
  };

  // FR-7: fetches the fresh, identity-wide company list on dropdown open. Silently keeps the
  // stale (login-time) list on failure — the dropdown stays usable either way.
  const handleTenantDropdownOpen = async () => {
    try {
      const response = await getCompaniesWithAccess();
      setFreshCompaniesWithAccess(response.data.companiesWithAccess);
    } catch {
      // keep the stale list
    }
  };

  const mergedCompaniesWithAccess = freshCompaniesWithAccess ?? user.companiesWithAccess;

  const selectedCompany = getSelectedObject(mergedCompaniesWithAccess, selectedTenant);
  const orderedCompanies = [...(mergedCompaniesWithAccess ?? [])].sort((a, b) => {
    if (a?.name === 'Virtual Officer') return -1;
    if (b?.name === 'Virtual Officer') return 1;
    if (selectedCompany && a?._id?.toString() === selectedCompany._id?.toString()) return -1;
    if (selectedCompany && b?._id?.toString() === selectedCompany._id?.toString()) return 1;
    const localeMap = { pt: 'pt-BR', en: 'en', es: 'es' };
    return (a?.name ?? '').localeCompare(b?.name ?? '', localeMap[i18n.language] || 'en', { sensitivity: 'base' });
  });

  const { data: totalUserNotifications } = useQuery(
    ['getTotalCountUserNotification', user.userId, selectedTenant?.slug],
    async () => {
      if (!selectedTenant?.slug) return 0;
      const result = await getTotalCountUserNotification(user.userId, selectedTenant.slug);
      return result?.data?.total || 0;
    }
  );

  const { data: notifications } = useQuery(
    ['getNotifications', user.userId, selectedTenant?.slug, totalUserNotifications],
    async () => {
      if (!selectedTenant?.slug) return [];
      if (!totalUserNotifications) return [];
      const result = await getUserNotifications(user.userId, selectedTenant.slug, 1, 5, '');
      return result?.data?.data || [];
    },
    { enabled: !!totalUserNotifications }
  );

  const isAlternateTenant = selectedTenant != null && selectedTenant?.slug !== getTenant();
  // FR-8: deliberately reads from the login-time user.companiesWithAccess, never the FR-7
  // refresh response — the refresh response never carries `partner`, only `name`.
  const mainTenantCompany = user.companiesWithAccess?.find(c => c.slug === getTenant()) ?? null;
  const eligibleToSwitchTenant = canSwitchTenant(user.roleActive, mainTenantCompany);

  return (
    <>
      <Main $openMenu={openMenu}>
        <HamburgerButton data-sidebar-toggle onClick={() => dispatch(changeStatusMenu())} aria-label="Toggle menu">
          <HamburgerSvg />
        </HamburgerButton>

        {(isAlternateTenant || isSwitching || switchError) && (
          <TenantChip>
            <BuildingSvg />
            {switchError
              ? t('header.switchTenantError', { name: switchTarget?.name })
              : t('header.accessing', { name: (isSwitching ? switchTarget : selectedTenant)?.name })}
            {isSwitching && <DotsLoader />}
          </TenantChip>
        )}

        <RightSection>
          <LanguageSelector />

          <div ref={notifRef} style={{ position: 'relative' }}>
            <Button
              iconOnly
              variant="icon-filled"
              size="lg"
              onClick={() => setNotifOpen(v => !v)}
              aria-label={t('header.notificationLabel', { count: totalUserNotifications || 0 })}
            >
              <BellSvg />
              <NotifBadge $show={!!totalUserNotifications}>
                {totalUserNotifications > 99 ? '99+' : totalUserNotifications}
              </NotifBadge>
            </Button>
            <NotificationDropdown $open={notifOpen}>
              <NotifDropdownHeader>
                <span className="title">{t('header.notifications')}</span>
                {!!totalUserNotifications && (
                  <span className="count">{totalUserNotifications}</span>
                )}
              </NotifDropdownHeader>
              {(!notifications || notifications.length === 0) && (
                <NotifEmptyState>{t('header.noNotifications')}</NotifEmptyState>
              )}
              {notifications?.map((n, i) => (
                <NotifItem key={n._id || i} onClick={() => { setNotifOpen(false); navigate('/user-notifications'); }}>
                  <NotifItemBody>
                    <div className="title">{n.titleNotification}</div>
                    <div className="body">{n.bodyNotification}</div>
                    <div className="time">{formatDateTimeToDateTimeBR(n.createdDate, true)}</div>
                  </NotifItemBody>
                </NotifItem>
              ))}
              {notifications?.length > 0 && (
                <Button
                  variant="text"
                  onClick={() => { setNotifOpen(false); navigate('/user-notifications'); }}
                  sx={{ display: 'block', width: '100%', padding: '12px 16px', fontFamily: "'Maven Pro', sans-serif", fontSize: '13px', fontWeight: 500, textAlign: 'center', borderRadius: 0 }}
                >
                  {t('header.viewAll')} →
                </Button>
              )}
            </NotificationDropdown>
          </div>

          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <UserButton onClick={() => setUserMenuOpen(v => !v)}>
              <UserInitialsAvatar>{getInitials(user?.name)}</UserInitialsAvatar>
              <UserName>{user?.name ?? t('header.defaultUser')}</UserName>
              <ChevronSvg />
            </UserButton>
            <UserDropdown $open={userMenuOpen}>
              <DropdownUserInfo>
                <DropdownUserAvatar>{getInitials(user?.name)}</DropdownUserAvatar>
                <DropdownUserMeta>
                  <div className="name">{user?.name ?? t('header.defaultUser')}</div>
                  <div className="email">{user?.email ?? ''}</div>
                </DropdownUserMeta>
              </DropdownUserInfo>

              {eligibleToSwitchTenant && mergedCompaniesWithAccess?.length > 0 && (
                <CompanySelectWrapper>
                  <CompanySelectLabel htmlFor="tenant-select">{t('header.company')}</CompanySelectLabel>
                  <Select
                    name="tenant-select"
                    value={selectedCompany?._id || ''}
                    disabled={isSwitching}
                    onOpen={handleTenantDropdownOpen}
                    onChange={value => {
                      const company = orderedCompanies.find(c => c._id === value);
                      handleSwitchCompany(company);
                    }}
                    options={orderedCompanies.map(item => ({ value: item._id, label: item.name }))}
                  />
                </CompanySelectWrapper>
              )}

              <LogoutItem onClick={() => { setUserMenuOpen(false); onLogoff(); }}>
                <LogoutSvg />
                {t('header.logout')}
              </LogoutItem>
            </UserDropdown>
          </div>
        </RightSection>
      </Main>

      {isAlternateTenant && (
        <TenantBanner $openMenu={openMenu}>
          <WarningSvg />
          <div>
            <BannerText>{t('header.accessingCompany', { name: selectedTenant?.name })}</BannerText>
            <br />
            <BannerSub>
              {switchError
                ? t('header.switchTenantError', { name: switchTarget?.name })
                : isSwitching
                  ? <DotsLoader />
                  : t('header.tenantBannerSub')}
            </BannerSub>
          </div>
          <BannerBackButton
            type="button"
            disabled={isSwitching}
            onClick={() => handleSwitchCompany(mainTenantCompany)}
          >
            {t('header.backToMyTenant')}
          </BannerBackButton>
        </TenantBanner>
      )}
    </>
  );
};
